/**
 * CytoscapeGraph Component
 *
 * Main graph visualization component using Cytoscape.js with WebGL rendering.
 * Replaces the D3-based GraphVisualization component.
 */

import {
  BrushCleaning,
  Check,
  Circle,
  Filter,
  GitBranch,
  Grid3X3,
  Layers,
  LayoutGrid,
  Loader2,
  Maximize2,
  Minimize2,
  Network,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  X,
} from 'lucide-react'
import type React from 'react'
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { useTheme } from '@/theme'
import { CanvasContextMenu } from './CanvasContextMenu'
import { type GraphTheme, getNodeColors } from './entity-icons'
import { fitPadding } from './fit-padding'
import { addEdgesSafely, safeBatch } from './safe-batch'
import {
  type GraphData,
  type GraphEdge,
  type GraphNode,
  type PathResult,
  type SelectedEdge,
  type SelectedNode,
  SPECTRE_COLORS,
} from './types'
import { useCanvasContextMenu } from './useCanvasContextMenu'
import { useCytoscapeContextMenu } from './useCytoscapeContextMenu'
import { useCytoscapeInstance } from './useCytoscapeInstance'
import { useCytoscapeLOD } from './useCytoscapeLOD'
import { useCytoscapePathFinding } from './useCytoscapePathFinding'
import { useCytoscapeSelection } from './useCytoscapeSelection'

// Graph layout options
const GRAPH_LAYOUTS = [
  { id: 'custom', name: 'Custom', icon: Maximize2, description: 'Manual positioning' },
  {
    id: 'fcose',
    name: 'Force-directed (fCoSE)',
    icon: Network,
    description: 'High-quality force-directed layout',
  },
  {
    id: 'clusters',
    name: 'Clusters (Louvain)',
    icon: Layers,
    description: 'Group nodes by community — best when Louvain has been computed',
  },
  {
    id: 'cola',
    name: 'Cola (Edge-weighted)',
    icon: Network,
    description: 'Edge-weighted spring-embedded layout',
  },
  { id: 'grid', name: 'Grid', icon: LayoutGrid, description: 'Organized grid pattern' },
  { id: 'circle', name: 'Circle', icon: Circle, description: 'Circular arrangement' },
  { id: 'breadthfirst', name: 'Hierarchical', icon: GitBranch, description: 'Tree-like hierarchy' },
  { id: 'concentric', name: 'Concentric', icon: Circle, description: 'Concentric circles' },
]

/**
 * Phone only (consumed by the `max-md:` classes on the layout popup): the popup
 * opens beside the toolbar and is clipped by the canvas's overflow-hidden
 * ancestors, so cap its height to the room left above the canvas bottom and let
 * it scroll inside.
 */
function capPopupToCanvas(popup: HTMLDivElement | null) {
  const canvas = popup?.closest<HTMLElement>('[data-graph-canvas-root]')
  if (!popup || !canvas) return
  const bottom = Math.min(canvas.getBoundingClientRect().bottom, window.innerHeight)
  const room = bottom - popup.getBoundingClientRect().top - 16
  popup.style.setProperty('--graph-popup-max-h', `${Math.max(room, 160)}px`)
}

export interface CytoscapeGraphProps {
  // Data
  nodes: GraphNode[]
  edges: GraphEdge[]

  // Callbacks
  onNodeSelect?: (node: SelectedNode | null) => void
  onEdgeSelect?: (edge: SelectedEdge | null) => void
  onMultiSelect?: (nodeIds: string[]) => void
  onLoadGroupChildren?: (groupId: string) => Promise<{ nodes: GraphNode[]; edges: GraphEdge[] }>
  onCollapseNode?: (
    nodeId: string,
    visibleNodeIds: string[]
  ) => Promise<{ nodesToHide: string[]; aggregatedEdges: GraphEdge[] }>
  /**
   * Add an entity by hand (the caller opens the schema-driven dialog). Shown as
   * a toolbar button, and as "Create entity here" when the empty canvas is
   * right-clicked — then with the graph position the new node should take.
   */
  onCreateEntity?: (position?: { x: number; y: number }) => void

  // Context menu callbacks
  onAddNote?: (nodeId: string) => void
  onAddFinding?: (nodeId: string) => void
  onAddConnection?: (nodeId: string) => void
  onExpandNode?: (nodeId: string) => Promise<void>
  onManageConnection?: (edgeId: string, sourceId: string, targetId: string) => void
  onAddGroupNote?: (groupId: string) => void
  onCreateGroup?: (nodeIds: string[]) => void
  onAddToSandbox?: (nodeId: string) => void
  onFindPathFromHere?: (nodeId: string) => void
  onToggleSuspect?: (nodeId: string) => void
  onToggleCrimeRelated?: (nodeId: string) => void
  /** Offered only on the entity types the CZ/SK registers index. */
  onLookUpInRegisters?: (nodeId: string) => void

  // Refresh callback
  onRefresh?: () => void
  isRefreshing?: boolean

  // Filter callbacks
  onToggleFilter?: () => void
  showFilterPanel?: boolean

  // Search callbacks
  nodeSearchQuery?: string
  onNodeSearchChange?: (query: string) => void
  nodeSearchResults?: Array<{ id: string; name: string; typeLabel?: string }>
  isNodeSearchOpen?: boolean
  onSearchNodeSelect?: (node: { id: string; name: string; typeLabel?: string }) => void
  onClearSearch?: () => void

  // Expand mode (fills parent's viewport)
  isExpanded?: boolean
  onToggleExpand?: () => void

  // Layout change callback
  onLayoutChange?: (layout: string) => void

  // Fires once when the underlying cy instance is initialised and the first
  // layout has settled. Use this to gate operations that need cy to be live
  // (e.g. applying a deep-linked path highlight on navigation).
  onReady?: () => void

  // Hide built-in toolbar (for when using external toolbar)
  hideBuiltInToolbar?: boolean

  // Extra toolbar buttons appended to the bottom of the built-in toolbar
  // column. Use this instead of absolute-positioning external buttons next to
  // the toolbar — those break whenever toolbar button count changes.
  toolbarExtras?: React.ReactNode

  // Forwarded to cytoscape: use texture rendering during pan/zoom. Enable on
  // surfaces that show many entities (explore-graph). Off by default.
  textureOnViewport?: boolean

  // Free-form per-case UI state (e.g. `{ layout: 'fcose' }`) included in every
  // saveLayout call. Consumer owns the shape.
  uiState?: Record<string, unknown> | null
  // Fires once after the initial saved layout state is fetched so consumers
  // can seed local state (e.g. selected layout) from `uiState`.
  onUiStateLoaded?: (uiState: Record<string, unknown> | null) => void

  // Filtering
  activeLabels?: Set<string>
  hiddenWorkflows?: Set<string>
  hiddenGroupIds?: Set<string>
  allGroups?: Array<{ id: string; nodeIds: string[] }>
  highlightedWorkflow?: string | null
  highlightedGroup?: string | null
  highlightedEdgeIds?: Set<string>
  highlightedGroupNodeIds?: Set<string>
  searchQuery?: string

  // Timeline ghost state: elements whose temporal timestamp is in the future
  // relative to the playhead. They are rendered at reduced opacity via the
  // `timeline-dimmed` CSS class and "light up" as the playhead passes them.
  dimmedNodeIds?: Set<string>
  dimmedEdgeIds?: Set<string>

  // Path finding state
  pathSourceId?: string | null
  isPathSelectMode?: boolean

  // Options
  layout?:
    | 'force'
    | 'hierarchical'
    | 'radial'
    | 'cose'
    | 'fcose'
    | 'cola'
    | 'clusters'
    | 'timeline'
    | 'grid'
    | 'circle'
    | 'breadthfirst'
    | 'concentric'
    | 'custom'
  enableLOD?: boolean
  enableWebGL?: boolean
  fitOnLoad?: boolean
  caseId?: string // For saving/loading node positions

  // Styling
  className?: string
  containerStyle?: React.CSSProperties

  // Legend
  legendLabels?: string[]
  activeLegendLabels?: Set<string>
  onLegendLabelClick?: (label: string) => void

  // Sidebar offset for legend
  sidebarWidth?: number
}

export interface CytoscapeGraphRef {
  // Navigation
  fitToView: () => void
  zoomIn: () => void
  zoomOut: () => void
  panToNode: (nodeId: string) => void
  resetView: () => void
  resize: () => void
  runLayout: () => void
  persist: () => void

  // Selection
  selectNode: (nodeId: string) => void
  selectNodeAndCenter: (nodeId: string) => void
  highlightNodes: (nodeIds: string[]) => void
  selectEdge: (edgeId: string) => void
  clearSelection: () => void

  // Path Finding — the shortest-path highlight the AI assistant drives.
  visualizePath: (pathResult: PathResult) => void
  clearPath: () => void

  // LOD
  expandGroup: (groupId: string) => Promise<void>
  collapseGroup: (groupId: string) => void

  // Data
  getNodeById: (id: string) => GraphNode | null
  getSelectedNodes: () => string[]

  // Export
  exportPng: () => string | null
  exportJson: () => object | null

  /**
   * Low-level escape hatch for callers that need to subscribe to Cytoscape
   * events not otherwise exposed (e.g. node/edge hover to drive the Bloom
   * popups from a parent view). Returns `null` if cy has not yet initialised.
   */
  getCy: () => cytoscape.Core | null
}

const CytoscapeGraph = forwardRef<CytoscapeGraphRef, CytoscapeGraphProps>(
  (
    {
      nodes,
      edges,
      onNodeSelect,
      onEdgeSelect,
      onMultiSelect,
      onLoadGroupChildren,
      onCollapseNode,
      onCreateEntity,
      onAddNote,
      onAddFinding,
      onAddConnection,
      onExpandNode,
      onManageConnection,
      onAddGroupNote,
      onCreateGroup,
      onAddToSandbox,
      onFindPathFromHere,
      onToggleSuspect,
      onToggleCrimeRelated,
      onLookUpInRegisters,
      onRefresh,
      isRefreshing,
      onToggleFilter,
      showFilterPanel,
      nodeSearchQuery,
      onNodeSearchChange,
      nodeSearchResults = [],
      isNodeSearchOpen,
      onSearchNodeSelect,
      onClearSearch,
      activeLabels,
      hiddenWorkflows,
      hiddenGroupIds,
      allGroups,
      highlightedWorkflow,
      highlightedEdgeIds,
      highlightedGroupNodeIds,
      searchQuery,
      dimmedNodeIds,
      dimmedEdgeIds,
      pathSourceId,
      isPathSelectMode,
      isExpanded,
      onToggleExpand,
      onLayoutChange,
      onReady,
      layout = 'force',
      className = '',
      containerStyle,
      legendLabels,
      activeLegendLabels,
      onLegendLabelClick,
      sidebarWidth = 0,
      caseId,
      hideBuiltInToolbar = false,
      toolbarExtras,
      textureOnViewport = false,
      uiState,
      onUiStateLoaded,
    },
    ref
  ) => {
    const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [isSearchExpanded, setIsSearchExpanded] = useState(false)
    const [isLayoutPopupOpen, setIsLayoutPopupOpen] = useState(false)
    const searchInputRef = useRef<HTMLInputElement>(null)
    const layoutPopupRef = useRef<HTMLDivElement>(null)
    const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null)

    // Resolve theme to 'dark' | 'light'
    const { theme: themeRaw } = useTheme()
    const resolvedTheme: GraphTheme = useMemo(() => {
      if (themeRaw === 'system') {
        return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
      }
      return themeRaw
    }, [themeRaw])
    const themeColors = useMemo(() => getNodeColors(resolvedTheme), [resolvedTheme])

    // Convert graph data to GraphData format
    const graphData: GraphData = useMemo(
      () => ({
        nodes,
        edges,
      }),
      [nodes, edges]
    )

    // Initialize Cytoscape instance
    const {
      cy,
      isReady,
      updateData,
      fitToView,
      resize,
      zoomIn,
      zoomOut,
      panToNode,
      setLassoEnabled,
      isLassoEnabled,
      runLayout,
      persist,
    } = useCytoscapeInstance({
      container: containerEl,
      data: graphData,
      layout,
      caseId,
      theme: resolvedTheme,
      textureOnViewport,
      uiState,
      onUiStateLoaded,
      onReady: () => {
        setIsLoading(false)
        if (loadingTimeoutRef.current) {
          clearTimeout(loadingTimeoutRef.current)
        }
        onReady?.()
      },
    })
    const { t } = useTranslation()
    const canvasMenu = useCanvasContextMenu(cy, Boolean(onCreateEntity))

    // Fallback timeout to prevent infinite loading
    useEffect(() => {
      loadingTimeoutRef.current = setTimeout(() => {
        if (isLoading) {
          setIsLoading(false)
        }
      }, 3000) // 3 second max loading time

      return () => {
        if (loadingTimeoutRef.current) {
          clearTimeout(loadingTimeoutRef.current)
        }
      }
    }, [isLoading]) // eslint-disable-line react-hooks/exhaustive-deps

    // Also hide loading when isReady changes
    useEffect(() => {
      if (isReady && isLoading) {
        setIsLoading(false)
      }
    }, [isReady, isLoading])

    // Close the layout popup when clicking outside
    useEffect(() => {
      const handleClickOutside = (e: MouseEvent) => {
        if (
          isLayoutPopupOpen &&
          layoutPopupRef.current &&
          !layoutPopupRef.current.contains(e.target as Node)
        ) {
          setIsLayoutPopupOpen(false)
        }
      }
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [isLayoutPopupOpen])

    // Selection hook
    const { selectedNodeIds, selectNode, selectEdge, clearSelection } = useCytoscapeSelection({
      cy,
      onNodeSelect,
      onEdgeSelect,
      onMultiSelect,
    })

    // Shortest-path highlight (driven by the AI assistant; also cleared on Escape).
    const { visualizePath, clearPath } = useCytoscapePathFinding({ cy })

    // Context menu for nodes and edges
    useCytoscapeContextMenu(cy, {
      onAddNote,
      onAddFinding,
      onAddConnection,
      onExpandNode,
      onManageConnection,
      onAddGroupNote,
      onCreateGroup,
      onAddToSandbox,
      onFindPathFromHere,
      onToggleSuspect,
      onToggleCrimeRelated,
      onLookUpInRegisters,
    })

    // Double-click to expand/collapse nodes
    useEffect(() => {
      if (!cy || !isReady) return

      const handleDoubleClick = async (evt: { target: cytoscape.NodeSingular }) => {
        const node = evt.target
        if (node.data('typeLabel') === 'SourceSnippet') return
        const hasCollapsedChildren = node.data('hasCollapsedChildren')

        // EXPAND: Node has collapsed children - expand them
        if (hasCollapsedChildren && onLoadGroupChildren) {
          node.addClass('loading')

          try {
            const { nodes: newNodes, edges: newEdges } = await onLoadGroupChildren(node.id())

            if (!cy) return

            safeBatch(cy, () => {
              // Add new nodes
              newNodes.forEach(n => {
                if (cy.$id(n.id).length === 0) {
                  const typeLabel = n.typeLabel || n.labels?.[0] || 'Default'
                  const displayName = n.displayName || n.name || n.id
                  const baseSize = 35

                  cy.add({
                    group: 'nodes',
                    data: {
                      id: n.id,
                      name: n.name || displayName,
                      displayName,
                      shortLabel:
                        displayName.length > 20 ? `${displayName.substring(0, 18)}…` : displayName,
                      typeLabel,
                      labels: n.labels || [typeLabel],
                      properties: n.properties || {},
                      hasCollapsedChildren: n.hasCollapsedChildren || false,
                      collapsedChildCount: n.collapsedChildCount || 0,
                      baseWidth: baseSize,
                      baseHeight: baseSize,
                      expandedFrom: node.id(),
                    },
                    position: {
                      x: node.position().x + (Math.random() - 0.5) * 200,
                      y: node.position().y + (Math.random() - 0.5) * 200,
                    },
                  })
                }
              })

              // Add new edges (addEdgesSafely drops any whose endpoint node is absent)
              addEdgesSafely(
                cy,
                newEdges
                  .filter(e => cy.$id(e.id).length === 0)
                  .map(e => ({
                    group: 'edges' as const,
                    data: {
                      id: e.id,
                      source: e.source,
                      target: e.target,
                      type: e.type,
                      properties: e.properties || {},
                      // Spread aggregated properties to top-level for CSS selectors
                      aggregated: e.properties?.aggregated || false,
                      originalType: e.properties?.originalType || e.type,
                      childCount: e.properties?.childCount || 0,
                      collapsedParentId: e.properties?.collapsedParentId || null,
                    },
                  }))
              )

              // Update parent node - remove collapsed state
              node.data('hasCollapsedChildren', false)
              node.data('collapsedChildCount', 0)
              node.addClass('expanded')
              node.removeClass('loading')

              // Remove aggregated edges from this parent
              cy.edges().forEach((edge: cytoscape.EdgeSingular) => {
                if (edge.data('aggregated') && edge.data('collapsedParentId') === node.id()) {
                  edge.remove()
                }
              })
            })

            // Run layout for new nodes around the parent
            const newNodeElements = cy
              .nodes()
              .filter((n: cytoscape.NodeSingular) => newNodes.some(nn => nn.id === n.id()))
            if (newNodeElements.length > 0) {
              const parentPos = node.position()
              const nodeCount = newNodeElements.length

              // Calculate radius based on number of children - larger for more children
              const baseRadius = Math.max(120, Math.sqrt(nodeCount) * 45)

              // Position new nodes in a circle around the parent
              newNodeElements.forEach((n: cytoscape.NodeSingular, i: number) => {
                const angle = (2 * Math.PI * i) / nodeCount - Math.PI / 2 // Start from top
                const radiusOffset = (Math.random() - 0.5) * 20
                n.position({
                  x: parentPos.x + (baseRadius + radiusOffset) * Math.cos(angle),
                  y: parentPos.y + (baseRadius + radiusOffset) * Math.sin(angle),
                })
              })

              // Run layout ONLY on new nodes + parent to spread them nicely
              // Don't touch the rest of the graph
              const layoutElements = newNodeElements.union(node)
              const relevantEdges = cy
                .edges()
                .filter(
                  (e: cytoscape.EdgeSingular) =>
                    layoutElements.contains(e.source()) && layoutElements.contains(e.target())
                )

              // Lock the parent position
              node.lock()

              layoutElements
                .union(relevantEdges)
                .layout({
                  name: 'cose',
                  animate: true,
                  animationDuration: 500,
                  animationEasing: 'ease-out-cubic',
                  fit: false,
                  randomize: false,
                  nodeRepulsion: () => 8000, // Increased from 3000
                  idealEdgeLength: () => baseRadius * 1.2, // Increased from 0.8
                  edgeElasticity: () => 45, // Reduced from 80 for less pull
                  gravity: 0.2, // Reduced from 0.4
                  numIter: 150, // Increased from 80
                  initialTemp: 150,
                  coolingFactor: 0.95,
                  boundingBox: {
                    x1: parentPos.x - baseRadius * 2.5, // Larger bounding box
                    y1: parentPos.y - baseRadius * 2.5,
                    x2: parentPos.x + baseRadius * 2.5,
                    y2: parentPos.y + baseRadius * 2.5,
                  },
                } as cytoscape.LayoutOptions)
                .run()

              // Unlock parent after layout completes
              setTimeout(() => {
                node.unlock()
              }, 600)
            }
          } catch (error) {
            console.error('Failed to expand node:', error)
            node.removeClass('loading')
          }
          return // Exit after expand
        }

        // COLLAPSE: Node has children in graph - collapse them
        if (onCollapseNode) {
          // Find if this node has outgoing edges to non-aggregated nodes
          const outgoingEdges = cy
            .edges()
            .filter(
              (e: cytoscape.EdgeSingular) => e.source().id() === node.id() && !e.data('aggregated')
            )

          if (outgoingEdges.length === 0) return // No children to collapse

          node.addClass('loading')

          try {
            // Get all visible node IDs
            const visibleNodeIds = cy.nodes().map((n: cytoscape.NodeSingular) => n.id())

            const { nodesToHide, aggregatedEdges } = await onCollapseNode(node.id(), visibleNodeIds)

            if (!cy || nodesToHide.length === 0) {
              node.removeClass('loading')
              return
            }

            safeBatch(cy, () => {
              // Remove nodes that should be hidden
              const nodesToRemove = cy
                .nodes()
                .filter((n: cytoscape.NodeSingular) => nodesToHide.includes(n.id()))
              nodesToRemove.remove()

              // Add aggregated edges (addEdgesSafely drops any whose endpoint is gone)
              addEdgesSafely(
                cy,
                aggregatedEdges
                  .filter(e => cy.$id(e.id).length === 0)
                  .map(e => ({
                    group: 'edges' as const,
                    data: {
                      id: e.id,
                      source: e.source,
                      target: e.target,
                      type: e.type,
                      properties: e.properties || {},
                      aggregated: true,
                      collapsedParentId: node.id(),
                    },
                  }))
              )

              // Mark node as having collapsed children
              node.data('hasCollapsedChildren', true)
              node.data('collapsedChildCount', nodesToHide.length)
              node.removeClass('expanded')
              node.removeClass('loading')
            })
          } catch (error) {
            console.error('Failed to collapse node:', error)
            node.removeClass('loading')
          }
        }
      }

      cy.on('dbltap', 'node', handleDoubleClick)

      return () => {
        cy.off('dbltap', 'node', handleDoubleClick)
      }
    }, [cy, isReady, onLoadGroupChildren, onCollapseNode])

    // Edge hover - show label on mouseover
    useEffect(() => {
      if (!cy || !isReady) return

      const handleEdgeMouseOver = (evt: { target: cytoscape.EdgeSingular }) => {
        evt.target.addClass('hover')
      }
      const handleEdgeMouseOut = (evt: { target: cytoscape.EdgeSingular }) => {
        evt.target.removeClass('hover')
      }

      cy.on('mouseover', 'edge', handleEdgeMouseOver)
      cy.on('mouseout', 'edge', handleEdgeMouseOut)

      return () => {
        cy.off('mouseover', 'edge', handleEdgeMouseOver)
        cy.off('mouseout', 'edge', handleEdgeMouseOut)
      }
    }, [cy, isReady])

    // LOD (Level of Detail) - controls labels, icons, type labels based on zoom
    useCytoscapeLOD({
      cy: isReady ? cy : null,
      enabled: true,
    })

    // Drag parent with children - move children along with parent
    useEffect(() => {
      if (!cy || !isReady) return

      let draggedNode: cytoscape.NodeSingular | null = null
      let childNodes: cytoscape.NodeCollection | null = null
      const relativePositions: Map<string, { dx: number; dy: number }> = new Map()

      const handleGrab = (evt: { target: cytoscape.NodeSingular }) => {
        draggedNode = evt.target
        const nodeId = draggedNode.id()

        // Find all children expanded from this node
        childNodes = cy
          .nodes()
          .filter((n: cytoscape.NodeSingular) => n.data('expandedFrom') === nodeId)

        if (childNodes.length === 0) {
          childNodes = null
          return
        }

        // Store relative positions
        const parentPos = draggedNode.position()
        childNodes.forEach((child: cytoscape.NodeSingular) => {
          const childPos = child.position()
          relativePositions.set(child.id(), {
            dx: childPos.x - parentPos.x,
            dy: childPos.y - parentPos.y,
          })
        })
      }

      const handleDrag = (evt: { target: cytoscape.NodeSingular }) => {
        if (!draggedNode || !childNodes || childNodes.length === 0) return
        if (evt.target.id() !== draggedNode.id()) return

        const parentPos = draggedNode.position()

        // Move children with parent
        safeBatch(cy, () => {
          childNodes?.forEach((child: cytoscape.NodeSingular) => {
            const rel = relativePositions.get(child.id())
            if (rel) {
              child.position({
                x: parentPos.x + rel.dx,
                y: parentPos.y + rel.dy,
              })
            }
          })
        })
      }

      const handleFree = () => {
        draggedNode = null
        childNodes = null
        relativePositions.clear()
      }

      cy.on('grab', 'node', handleGrab)
      cy.on('drag', 'node', handleDrag)
      cy.on('free', 'node', handleFree)

      return () => {
        cy.off('grab', 'node', handleGrab)
        cy.off('drag', 'node', handleDrag)
        cy.off('free', 'node', handleFree)
      }
    }, [cy, isReady])

    // Note: previously a `cy.on('add', 'node', ...)` listener re-ran fcose
    // over the entire graph on every node insertion — that scrambled all
    // existing positions when expanding neighbors. Removed. New nodes are
    // placed by `useCytoscapeInstance.updateData` (isolated cose on the
    // newly-added subset only), which leaves existing nodes untouched.

    // Apply filters when hiddenWorkflows or hiddenGroupIds change
    // NOTE: activeLabels filtering is now done upstream in CaseGraphExplorer via exclusionState
    useEffect(() => {
      if (!cy || !isReady) return

      safeBatch(cy, () => {
        // Show all first
        cy.nodes().removeClass('filtered-out')
        cy.edges().removeClass('filtered-out')

        // Hide nodes from hidden workflows
        if (hiddenWorkflows && hiddenWorkflows.size > 0) {
          cy.nodes().forEach((node: cytoscape.NodeSingular) => {
            const nodeWorkflowId = node.data('properties')?.workflow_run_id
            if (nodeWorkflowId && hiddenWorkflows.has(nodeWorkflowId)) {
              node.addClass('filtered-out')
            }
          })
        }

        // Hide nodes from hidden groups
        if (hiddenGroupIds && hiddenGroupIds.size > 0 && allGroups) {
          // Build a set of all node IDs that belong to hidden groups
          const hiddenNodeIds = new Set<string>()
          allGroups.forEach(group => {
            if (hiddenGroupIds.has(group.id)) {
              group.nodeIds.forEach(nodeId => hiddenNodeIds.add(nodeId))
            }
          })

          cy.nodes().forEach((node: cytoscape.NodeSingular) => {
            if (hiddenNodeIds.has(node.id())) {
              node.addClass('filtered-out')
            }
          })
        }

        // Hide edges whose source or target is hidden
        cy.edges().forEach((edge: cytoscape.EdgeSingular) => {
          const source = edge.source()
          const target = edge.target()
          if (source.hasClass('filtered-out') || target.hasClass('filtered-out')) {
            edge.addClass('filtered-out')
          }
        })
      })
    }, [cy, isReady, hiddenWorkflows, hiddenGroupIds, allGroups])

    // Apply edge highlighting (for selected node connections)
    useEffect(() => {
      if (!cy || !isReady) return

      safeBatch(cy, () => {
        // Remove previous highlighting
        cy.edges().removeClass('edge-highlighted')

        // Add highlighting to specified edges
        if (highlightedEdgeIds && highlightedEdgeIds.size > 0) {
          cy.edges().forEach((edge: cytoscape.EdgeSingular) => {
            const edgeId = edge.data('id')
            if (highlightedEdgeIds.has(edgeId)) {
              edge.addClass('edge-highlighted')
            }
          })
        }
      })
    }, [cy, isReady, highlightedEdgeIds])

    // Apply group node highlighting and zoom to highlighted nodes
    useEffect(() => {
      if (!cy || !isReady) {
        console.log('[Group Highlight] Skipping - cy ready:', !!cy, 'isReady:', isReady)
        return
      }

      console.log('[Group Highlight] Processing highlights:', {
        count: highlightedGroupNodeIds?.size || 0,
        ids: highlightedGroupNodeIds ? Array.from(highlightedGroupNodeIds) : [],
        totalNodes: cy.nodes().length,
      })

      safeBatch(cy, () => {
        // Remove previous group highlighting
        cy.nodes().removeClass('group-highlighted')

        // Add highlighting to specified nodes
        if (highlightedGroupNodeIds && highlightedGroupNodeIds.size > 0) {
          const highlightedNodes: cytoscape.NodeCollection = cy.collection()
          let foundCount = 0

          cy.nodes().forEach((node: cytoscape.NodeSingular) => {
            const nodeId = node.data('id')
            if (highlightedGroupNodeIds.has(nodeId)) {
              node.addClass('group-highlighted')
              highlightedNodes.merge(node)
              foundCount++
            }
          })

          console.log('[Group Highlight] Applied to', foundCount, 'nodes')

          // Zoom to highlighted nodes with animation
          if (highlightedNodes.length > 0) {
            // First fit to the highlighted nodes
            setTimeout(() => {
              cy.animate(
                {
                  fit: {
                    eles: highlightedNodes,
                    padding: fitPadding(80),
                  },
                  duration: 600,
                  easing: 'ease-in-out-cubic',
                },
                {
                  complete: () => {
                    // Ensure highlighted nodes are visible and centered
                    cy.center(highlightedNodes)
                  },
                }
              )
            }, 100) // Small delay to ensure nodes are rendered
          } else {
            console.warn('[Group Highlight] No nodes found to highlight!')
          }
        }
      })
    }, [cy, isReady, highlightedGroupNodeIds])

    // Apply workflow highlighting
    useEffect(() => {
      if (!cy || !isReady) return

      safeBatch(cy, () => {
        // Remove previous workflow highlighting
        cy.nodes().removeClass('workflow-highlighted')

        // Add highlighting to nodes with matching workflow_run_id
        if (highlightedWorkflow) {
          cy.nodes().forEach((node: cytoscape.NodeSingular) => {
            const nodeWorkflowId = node.data('properties')?.workflow_run_id
            if (nodeWorkflowId && String(nodeWorkflowId) === highlightedWorkflow) {
              node.addClass('workflow-highlighted')
            }
          })
        }
      })
    }, [cy, isReady, highlightedWorkflow])

    // Apply search filter
    useEffect(() => {
      if (!cy || !isReady) return

      safeBatch(cy, () => {
        cy.nodes().removeClass('search-hidden search-highlight')

        if (searchQuery && searchQuery.length > 0) {
          const query = searchQuery.toLowerCase()
          cy.nodes().forEach((node: cytoscape.NodeSingular) => {
            const name = String(node.data('displayName') || node.data('name') || '').toLowerCase()
            if (name.includes(query)) {
              node.addClass('search-highlight')
            } else {
              node.addClass('search-hidden')
            }
          })
        }
      })
    }, [cy, isReady, searchQuery])

    // Apply path source blink effect
    useEffect(() => {
      if (!cy || !isReady) return

      safeBatch(cy, () => {
        // Remove previous path source styling
        cy.nodes().removeClass('path-source-blink path-target-select')

        // Add blink to path source
        if (pathSourceId && isPathSelectMode) {
          const sourceNode = cy.$id(pathSourceId)
          if (sourceNode.length > 0) {
            sourceNode.addClass('path-source-blink')
          }

          // Add target-select class to all other nodes (visual hint)
          cy.nodes().not(sourceNode).addClass('path-target-select')
        }
      })
    }, [cy, isReady, pathSourceId, isPathSelectMode])

    // Update data when nodes/edges change
    useEffect(() => {
      if (!cy || !isReady) return
      updateData(graphData)
    }, [cy, isReady, graphData, updateData])

    // Apply timeline-driven dim state. Runs after updateData so that newly
    // added elements get tagged with the `timeline-dimmed` class immediately.
    useEffect(() => {
      if (!cy || !isReady) return
      safeBatch(cy, () => {
        cy.nodes().removeClass('timeline-dimmed')
        cy.edges().removeClass('timeline-dimmed')
        if (dimmedNodeIds && dimmedNodeIds.size > 0) {
          cy.nodes().forEach((n: cytoscape.NodeSingular) => {
            if (dimmedNodeIds.has(n.id())) n.addClass('timeline-dimmed')
          })
        }
        if (dimmedEdgeIds && dimmedEdgeIds.size > 0) {
          cy.edges().forEach((e: cytoscape.EdgeSingular) => {
            if (dimmedEdgeIds.has(e.id())) e.addClass('timeline-dimmed')
          })
        }
      })
    }, [cy, isReady, dimmedNodeIds, dimmedEdgeIds, graphData])

    // Reset view
    const resetView = useCallback(() => {
      if (!cy) return
      cy.reset()
      fitToView()
    }, [cy, fitToView])

    // Get node by ID
    const getNodeById = useCallback(
      (id: string): GraphNode | null => {
        return nodes.find(n => n.id === id) || null
      },
      [nodes]
    )

    // Get selected node IDs
    const getSelectedNodes = useCallback((): string[] => {
      return Array.from(selectedNodeIds)
    }, [selectedNodeIds])

    // Stub functions for LOD (to be implemented with useCytoscapeLOD)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const expandGroup = useCallback(async (_groupId: string) => {
      // TODO: implement with useCytoscapeLOD
    }, [])

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const collapseGroup = useCallback((_groupId: string) => {
      // TODO: implement with useCytoscapeLOD
    }, [])

    // Export as PNG
    const exportPng = useCallback((): string | null => {
      if (!cy) return null
      return cy.png({
        output: 'base64uri',
        bg: '#1a1a1a',
        full: true,
        scale: 2,
      })
    }, [cy])

    // Export as JSON
    const exportJson = useCallback((): object | null => {
      if (!cy) return null
      return cy.json()
    }, [cy])

    // Select node, highlight its edges, and center+zoom on it with animation
    const selectNodeAndCenter = useCallback(
      (nodeId: string) => {
        if (!cy) return

        selectNode(nodeId)

        const node = cy.$id(nodeId)
        if (node.length === 0) return

        // Fit to the node + its direct neighbors so the local neighborhood is visible
        const neighborhood = node.closedNeighborhood()
        cy.animate({
          fit: { eles: neighborhood, padding: fitPadding(80) },
          duration: 200,
          easing: 'ease-in-out-quad',
        })
      },
      [cy, selectNode]
    )

    // Highlight multiple nodes: dim everything except those nodes + their shared neighborhood
    const highlightNodes = useCallback(
      (nodeIds: string[]) => {
        if (!cy) return
        cy.elements().unselect()
        cy.elements().addClass('focus-dimmed')
        let combined = cy.collection()
        nodeIds.forEach(id => {
          const node = cy.$id(id)
          if (node.length > 0) {
            node.select()
            combined = combined.union(node.closedNeighborhood())
          }
        })
        combined.removeClass('focus-dimmed')
        if (combined.length > 0) {
          cy.animate({
            fit: { eles: combined, padding: fitPadding(80) },
            duration: 400,
            easing: 'ease-in-out-quad',
          })
        }
      },
      [cy]
    )

    // Expose methods via ref
    useImperativeHandle(
      ref,
      () => ({
        fitToView,
        zoomIn,
        zoomOut,
        panToNode,
        resetView,
        resize,
        selectNode,
        selectNodeAndCenter,
        highlightNodes,
        selectEdge,
        clearSelection,
        visualizePath,
        clearPath,
        expandGroup,
        collapseGroup,
        getNodeById,
        getSelectedNodes,
        exportPng,
        exportJson,
        runLayout,
        persist,
        getCy: () => cy,
      }),
      [
        fitToView,
        zoomIn,
        zoomOut,
        panToNode,
        resetView,
        resize,
        selectNode,
        selectNodeAndCenter,
        highlightNodes,
        selectEdge,
        clearSelection,
        visualizePath,
        clearPath,
        expandGroup,
        collapseGroup,
        getNodeById,
        getSelectedNodes,
        exportPng,
        exportJson,
        runLayout,
        persist,
        cy,
      ]
    )

    // Keyboard shortcuts
    useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
        const activeElement = document.activeElement
        if (activeElement?.tagName === 'INPUT' || activeElement?.tagName === 'TEXTAREA') {
          return
        }

        switch (e.key) {
          case 'Escape':
            // Dismiss any lingering shortest-path highlight (ring-menu Find
            // Path tool or the AI assistant's Highlight action) along with the
            // selection — neither has other dismissal chrome.
            clearPath()
            clearSelection()
            break
          case '+':
          case '=':
            if (e.metaKey || e.ctrlKey) {
              e.preventDefault()
              zoomIn()
            }
            break
          case '-':
            if (e.metaKey || e.ctrlKey) {
              e.preventDefault()
              zoomOut()
            }
            break
          case '0':
            if (e.metaKey || e.ctrlKey) {
              e.preventDefault()
              resetView()
            }
            break
          case 'f':
            if (e.metaKey || e.ctrlKey) {
              e.preventDefault()
              fitToView()
            }
            break
        }
      }

      window.addEventListener('keydown', handleKeyDown)
      return () => window.removeEventListener('keydown', handleKeyDown)
    }, [clearPath, clearSelection, zoomIn, zoomOut, resetView, fitToView])

    // Get color for legend
    const getColor = (type: string): string => {
      const colors = SPECTRE_COLORS[type as keyof typeof SPECTRE_COLORS]
      if (colors && typeof colors === 'object' && 'bg' in colors) {
        return colors.bg
      }
      return SPECTRE_COLORS.Default.bg
    }

    return (
      <div
        data-graph-canvas-root
        className={`relative w-full h-full ${className}`}
        style={containerStyle}
      >
        {/* Toolbar - top right corner, vertical layout. Position respects the
            ancestor-supplied `--graph-tools-right` CSS variable so external
            chrome (e.g. the investigation sidebar) can push it left without
            modifying this component. */}
        {!hideBuiltInToolbar && (
          <div
            data-graph-toolbar
            className="absolute top-4 z-20 flex flex-col gap-2 items-end transition-[right] duration-300"
            style={{ right: 'var(--graph-tools-right, 1rem)' }}
          >
            {/* Search input - expandable with suggestions below */}
            {onNodeSearchChange && (
              <div className="flex flex-col items-end gap-2 max-md:relative">
                {isSearchExpanded ? (
                  <>
                    <div className="flex items-center gap-1 bg-popover rounded-lg border border-border overflow-hidden shadow-lg animate-in slide-in-from-right-2 duration-200">
                      <div className="relative">
                        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                        <input
                          ref={searchInputRef}
                          type="text"
                          placeholder="Search nodes..."
                          value={nodeSearchQuery || ''}
                          onChange={e => onNodeSearchChange(e.target.value)}
                          className="w-48 h-9 pl-9 pr-2 bg-transparent text-foreground text-base placeholder:text-muted-foreground focus:outline-none md:text-sm"
                        />
                      </div>
                      <button
                        onClick={() => {
                          setIsSearchExpanded(false)
                          onClearSearch?.()
                        }}
                        className="p-2 text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>

                    {/* Search results - shown below input, max 5. On the phone the
                        list floats over the toolbar instead of pushing the buttons
                        halfway down the canvas. */}
                    {isNodeSearchOpen && nodeSearchResults.length > 0 && onSearchNodeSelect && (
                      <div className="w-full bg-popover border border-border rounded-lg shadow-lg overflow-hidden max-md:absolute max-md:top-full max-md:right-0 max-md:z-10 max-md:mt-2">
                        {nodeSearchResults.slice(0, 5).map(node => (
                          <div
                            key={node.id}
                            className="p-2.5 hover:bg-accent cursor-pointer text-sm border-b border-border last:border-0 transition-colors"
                            onClick={() => {
                              onSearchNodeSelect(node)
                              setIsSearchExpanded(false)
                            }}
                          >
                            <div className="font-medium text-foreground truncate">
                              {node.name || node.id}
                            </div>
                            <div className="text-xs text-muted-foreground">{node.typeLabel}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <button
                    onClick={() => {
                      setIsSearchExpanded(true)
                      setTimeout(() => searchInputRef.current?.focus(), 100)
                    }}
                    className="p-2 rounded-lg transition-colors bg-background border border-border text-muted-foreground hover:text-foreground hover:bg-accent"
                    title="Search nodes"
                  >
                    <Search className="h-5 w-5" />
                  </button>
                )}
              </div>
            )}

            {/* Refresh button */}
            {onRefresh && (
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={onRefresh}
                      disabled={isRefreshing}
                      className={`p-2 rounded-lg transition-colors ${
                        isRefreshing
                          ? 'bg-background border border-border text-blue-500 dark:text-blue-400 cursor-not-allowed'
                          : 'bg-background border border-border text-muted-foreground hover:text-foreground hover:bg-accent'
                      }`}
                    >
                      <RefreshCw className={`h-5 w-5 ${isRefreshing ? 'animate-spin' : ''}`} />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="left">Refresh graph</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {/* Filter button */}
            {onToggleFilter && (
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={onToggleFilter}
                      className={`p-2 rounded-lg transition-colors flex-shrink-0 ${
                        showFilterPanel
                          ? 'bg-blue-600 text-white'
                          : 'bg-background border border-border text-muted-foreground hover:text-foreground hover:bg-accent'
                      }`}
                    >
                      <Filter className="h-5 w-5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="left">Filter by node type</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {/* Lasso selection button */}
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => setLassoEnabled(!isLassoEnabled)}
                    className={`p-2 rounded-lg transition-colors ${
                      isLassoEnabled
                        ? 'bg-amber-600 text-white'
                        : 'bg-background border border-border text-muted-foreground hover:text-foreground hover:bg-accent'
                    }`}
                  >
                    <Pencil className="h-5 w-5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="left">
                  {isLassoEnabled
                    ? 'Lasso mode ON — draw to select nodes'
                    : 'Enable lasso selection'}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            {/* Expand/Collapse button */}
            {onToggleExpand && (
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={onToggleExpand}
                      className={`p-2 rounded-lg transition-colors ${
                        isExpanded
                          ? 'bg-purple-600 text-white'
                          : 'bg-background border border-border text-muted-foreground hover:text-foreground hover:bg-accent'
                      }`}
                    >
                      {isExpanded ? (
                        <Minimize2 className="h-5 w-5" />
                      ) : (
                        <Maximize2 className="h-5 w-5" />
                      )}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="left">
                    {isExpanded ? 'Collapse view' : 'Expand view'}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {/* Layout selector button with expandable popup */}
            {onLayoutChange && (
              <div className="md:relative" ref={layoutPopupRef}>
                <TooltipProvider delayDuration={0}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => setIsLayoutPopupOpen(!isLayoutPopupOpen)}
                        className={`p-2 rounded-lg transition-colors ${
                          isLayoutPopupOpen
                            ? 'bg-green-600 text-white'
                            : 'bg-background border border-border text-muted-foreground hover:text-foreground hover:bg-accent'
                        }`}
                      >
                        <Grid3X3 className="h-5 w-5" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="left">Change graph layout</TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                {/* Layout options popup */}
                {isLayoutPopupOpen && (
                  <div
                    ref={capPopupToCanvas}
                    className="absolute right-12 top-0 w-64 bg-popover border border-border rounded-lg shadow-lg overflow-hidden animate-in slide-in-from-top-2 duration-200 max-md:max-h-[var(--graph-popup-max-h)] max-md:overflow-y-auto md:right-0 md:top-full md:mt-2"
                  >
                    <div className="px-3 py-2 border-b border-border">
                      <span className="text-xs text-muted-foreground uppercase tracking-wider font-medium">
                        Graph Layout
                      </span>
                    </div>
                    {GRAPH_LAYOUTS.map(layoutOption => {
                      const isActive = layout === layoutOption.id
                      const Icon = layoutOption.icon
                      return (
                        <div
                          key={layoutOption.id}
                          className={`px-3 py-2.5 cursor-pointer transition-colors hover:bg-accent ${
                            isActive ? 'bg-accent' : ''
                          }`}
                          onClick={() => {
                            onLayoutChange(layoutOption.id)
                            setIsLayoutPopupOpen(false)
                          }}
                        >
                          <div className="flex items-center gap-3">
                            <Icon className="h-4 w-4 text-muted-foreground" />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-foreground">
                                {layoutOption.name}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {layoutOption.description}
                              </div>
                            </div>
                            {isActive && <Check className="h-4 w-4 text-green-500 flex-shrink-0" />}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Tidy up — re-run the currently selected layout to clean up
                positions after drags. Only shown when a layout algorithm is
                active ('custom' / 'timeline' would no-op). */}
            {onLayoutChange && layout !== 'custom' && layout !== 'timeline' && (
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={runLayout}
                      className="p-2 rounded-lg transition-colors bg-background border border-border text-muted-foreground hover:text-foreground hover:bg-accent"
                    >
                      <BrushCleaning className="h-5 w-5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="left">Tidy up — re-run current layout</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {/* Create entity — opens the schema-driven dialog (manual-graph). */}
            {onCreateEntity && (
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => onCreateEntity()}
                      aria-label={t('manualGraph.canvasMenu.createEntity')}
                      className="p-2 rounded-lg transition-colors bg-background border border-border text-muted-foreground hover:text-foreground hover:bg-accent"
                    >
                      <Plus className="h-5 w-5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="left">
                    {t('manualGraph.canvasMenu.createEntity')}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {toolbarExtras}
          </div>
        )}

        {/* Lasso mode indicator - positioned to the left of toolbar */}
        {isLassoEnabled && (
          <div className="absolute top-[168px] right-16 z-10 bg-amber-600 text-white px-3 py-1.5 rounded-lg shadow-lg text-sm flex items-center gap-2">
            <Pencil className="h-3 w-3" />
            <span className="text-xs font-medium">Draw to select</span>
          </div>
        )}

        {/* Loading overlay */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/80 z-10">
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="text-sm text-muted-foreground">Loading graph...</span>
            </div>
          </div>
        )}

        {/* Graph container */}
        <div
          ref={setContainerEl}
          className="w-full h-full min-h-[240px] md:min-h-[400px]"
          style={{ background: themeColors.canvasBg }}
          onContextMenu={onCreateEntity ? e => e.preventDefault() : undefined}
        />

        {/* Empty-canvas context menu, anchored at the right-click. */}
        {onCreateEntity && (
          <CanvasContextMenu
            menu={canvasMenu.menu}
            onClose={canvasMenu.close}
            onCreateEntity={onCreateEntity}
          />
        )}

        {/* Legend */}
        {legendLabels && legendLabels.length > 0 && (
          <div
            className="absolute bottom-0 left-0 right-0 z-10 flex flex-wrap justify-center gap-2 bg-background/90 px-4 py-3"
            style={{ paddingRight: sidebarWidth > 0 ? `${sidebarWidth + 16}px` : undefined }}
          >
            {legendLabels.map(label => {
              // When activeLegendLabels is empty or undefined, all labels are shown (active)
              const isActive =
                !activeLegendLabels ||
                activeLegendLabels.size === 0 ||
                activeLegendLabels.has(label)
              return (
                <button
                  key={label}
                  onClick={() => onLegendLabelClick?.(label)}
                  className={`flex items-center gap-1 text-xs px-2 py-1 rounded transition-all ${
                    onLegendLabelClick ? 'hover:bg-neutral-700 cursor-pointer' : 'cursor-default'
                  } ${isActive ? 'opacity-100' : 'opacity-40'}`}
                  disabled={!onLegendLabelClick}
                >
                  <div
                    className="w-3 h-3 rounded-full border"
                    style={{
                      backgroundColor: getColor(label),
                      borderColor: getColor(label),
                    }}
                  />
                  <span className={`text-muted-foreground ${isActive ? '' : 'line-through'}`}>
                    {label}
                  </span>
                </button>
              )
            })}
            {/* Separator */}
            <div className="w-px h-4 bg-neutral-600 mx-1 self-center" />
            {/* Aggregated edge legend */}
            <div className="flex items-center gap-1 text-xs">
              <div
                className="w-6 h-0.5 bg-violet-500"
                style={{
                  backgroundImage:
                    'repeating-linear-gradient(90deg, #8b5cf6 0px, #8b5cf6 4px, transparent 4px, transparent 8px)',
                }}
              />
              <span className="text-muted-foreground">Aggregated</span>
            </div>
            {/* Collapsed children indicator */}
            <div className="flex items-center gap-1 text-xs">
              <div
                className="w-3 h-3 rounded-full border-2"
                style={{
                  borderColor: '#8b5cf6',
                  borderStyle: 'double',
                  backgroundColor: 'transparent',
                }}
              />
              <span className="text-muted-foreground">Expandable</span>
            </div>
          </div>
        )}
      </div>
    )
  }
)

CytoscapeGraph.displayName = 'CytoscapeGraph'

export default CytoscapeGraph
